// src/phase4/audio/SoundManager.ts
// Sound management for Phase 4 tower defense

import Phaser from 'phaser';
import { SOUND_CONFIGS, MUSIC_CONFIGS } from './audio-config';
import type { SoundConfig } from './SoundManager.types';

/** Sound manager class for managing all audio in Phase 4 */
export class SoundManager {
  private scene: Phaser.Scene;
  private sounds: Map<string, Phaser.Sound.NoAudioSound> = new Map();
  private music: Map<string, Phaser.Sound.NoAudioSound> = new Map();
  private muted: boolean = false;
  private masterVolume: number = 1.0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // Preload all audio assets
  static preload(scene: Phaser.Scene): void {
    // Preload sound effects
    SOUND_CONFIGS.forEach((config) => {
      scene.load.audio(config.key, config.path);
    });

    // Preload music
    MUSIC_CONFIGS.forEach((config) => {
      scene.load.audio(config.key, config.path);
    });
  }

  // Create all sounds after preloading
  create(): void {
    // Create sound effects
    SOUND_CONFIGS.forEach((config) => {
      this.createSound(config);
    });

    // Create music
    MUSIC_CONFIGS.forEach((config) => {
      this.createMusic(config);
    });
  }

  private createSound(config: SoundConfig): void {
    // Always create sound - Phaser handles missing audio gracefully
    const sound = this.scene.sound.add(config.key, {
      volume: (config.volume || 1) * this.masterVolume,
      rate: config.rate || 1,
    }) as Phaser.Sound.NoAudioSound;
    this.sounds.set(config.key, sound);
  }

  private createMusic(config: SoundConfig): void {
    const music = this.scene.sound.add(config.key, {
      volume: (config.volume || 1) * this.masterVolume,
      rate: config.rate || 1,
      loop: true,
    }) as Phaser.Sound.NoAudioSound;
    this.music.set(config.key, music);
  }

  // Play a sound effect
  play(key: string): void {
    if (this.muted) return;

    const sound = this.sounds.get(key);
    if (sound) {
      // Stop if already playing to allow rapid re-triggering
      if (sound.isPlaying) {
        sound.stop();
      }
      sound.play();
    } else {
      console.warn(`[SoundManager] Sound not found: ${key}`);
    }
  }

  // Play a sound with pitch variation
  playWithPitch(key: string, pitch: number): void {
    if (this.muted) return;

    const sound = this.sounds.get(key);
    if (sound) {
      sound.setRate(pitch);
      sound.play();
    }
  }

  // Play background music
  playMusic(key: string, fadeIn: boolean = false): void {
    if (this.muted) return;

    // Stop any playing music
    this.stopAllMusic();

    const music = this.music.get(key);
    if (music) {
      music.setLoop(true);
      if (fadeIn) {
        music.setVolume(0);
        music.play();
        this.scene.tweens.add({
          targets: music,
          volume: (music.volume as number) * this.masterVolume,
          duration: 1000,
        });
      } else {
        music.play();
      }
    }
  }

  // Stop specific sound
  stop(key: string): void {
    const sound = this.sounds.get(key);
    if (sound && sound.isPlaying) {
      sound.stop();
    }
  }

  // Stop all music
  stopAllMusic(): void {
    this.music.forEach((music) => {
      if (music.isPlaying) {
        music.stop();
      }
    });
  }

  // Stop all sounds
  stopAll(): void {
    this.sounds.forEach((sound) => {
      if (sound.isPlaying) {
        sound.stop();
      }
    });
    this.stopAllMusic();
  }

  // Set volume for a specific sound
  setVolume(key: string, volume: number): void {
    const sound = this.sounds.get(key);
    if (sound) {
      sound.setVolume(volume * this.masterVolume);
    }
  }

  // Set master volume
  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));

    // Update all sounds
    this.sounds.forEach((sound) => {
      sound.setVolume(this.getEffectiveVolume(sound));
    });

    // Update music
    this.music.forEach((music) => {
      music.setVolume(this.getEffectiveVolume(music));
    });
  }

  private getEffectiveVolume(sound: Phaser.Sound.NoAudioSound): number {
    // Extract base volume from sound config if available
    const baseVolume = (sound.volume as number) / this.masterVolume;
    return baseVolume * this.masterVolume;
  }

  // Mute/unmute all audio
  setMute(muted: boolean): void {
    this.muted = muted;
    this.scene.sound.mute = muted;
  }

  // Check if muted
  isMuted(): boolean {
    return this.muted;
  }

  // Play tower sounds
  playTowerShoot(type: 'cpu' | 'lang' | 'net' = 'cpu'): void {
    const key = `tower_shoot_${type}`;
    if (this.sounds.has(key)) {
      this.play(key);
    } else {
      this.play('tower_shoot');
    }
  }

  playTowerUpgrade(): void {
    this.play('tower_upgrade');
  }

  playTowerSelect(): void {
    this.play('tower_select');
  }

  // Play enemy sounds
  playEnemySpawn(): void {
    this.play('enemy_spawn');
  }

  playEnemyDamage(): void {
    this.playWithPitch('enemy_damage', 0.9 + Math.random() * 0.2);
  }

  playEnemyDie(): void {
    this.play('enemy_die');
  }

  // Play wave sounds
  playWaveStart(): void {
    this.play('wave_start');
  }

  playWaveComplete(): void {
    this.play('wave_complete');
  }

  playWaveAllComplete(): void {
    this.play('wave_all_complete');
  }

  // Play base sounds
  playBaseHit(): void {
    this.play('base_hit');
  }

  // Play UI sounds
  playUiClick(): void {
    this.play('ui_click');
  }

  playUiHover(): void {
    this.play('ui_hover');
  }

  playUiError(): void {
    this.play('ui_error');
  }

  playUiSuccess(): void {
    this.play('ui_success');
  }

  // Cleanup
  destroy(): void {
    this.stopAll();
    this.sounds.clear();
    this.music.clear();
  }
}

// Factory function
export function createSoundManager(scene: Phaser.Scene): SoundManager {
  const manager = new SoundManager(scene);
  manager.create();
  return manager;
}
